import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, ArrowRight, CheckCircle, Sparkles } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useAssessment } from '../../contexts/AssessmentContext';
import { supabase } from '../../lib/supabase';
import { ResultsGraphTR } from '../../components/ResultsGraphTR';
import { HarmonicGraphTR } from '../../components/HarmonicGraphTR';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import type { Score } from '../../types';
import type { HarmonicScore } from '../../types/harmonic';

export function ResultsViewTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { scores: contextScores, harmonicScores: contextHarmonicScores } = useAssessment();
  const [dbScores, setDbScores] = useState<Score[] | null>(null);
  const [dbHarmonicScores, setDbHarmonicScores] = useState<HarmonicScore[] | null>(null);
  const [loadingFromDb, setLoadingFromDb] = useState(false);

  const scores = contextScores || dbScores;
  const harmonicScores = contextHarmonicScores || dbHarmonicScores;

  const loadFromDatabase = useCallback(async (userId: string) => {
    setLoadingFromDb(true);
    try {
      const { data } = await supabase
        .from('results')
        .select('scores, harmonic_scores')
        .eq('user_id', userId)
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        setDbScores(data.scores as Score[]);
        setDbHarmonicScores(data.harmonic_scores as HarmonicScore[]);
      } else {
        navigate('/tr/assessment');
      }
    } catch {
      navigate('/tr/assessment');
    } finally {
      setLoadingFromDb(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/tr/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user || authLoading) return;

    if (!contextScores || !contextHarmonicScores) {
      loadFromDatabase(user.id);
    }
  }, [user, authLoading, contextScores, contextHarmonicScores, loadFromDatabase]);

  if (authLoading || loadingFromDb) {
    return <LoadingSpinner message="Sonuclar yukleniyor..." />;
  }

  if (!scores || !harmonicScores) {
    return <LoadingSpinner message="Sonuclar yukleniyor..." />;
  }

  return (
    <div className="min-h-screen freud-bg py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-sage-100 rounded-full text-sage-700 text-sm font-medium mb-4">
            <CheckCircle className="w-4 h-4" />
            Degerlendirme Tamamlandi
          </div>
          <h1 className="freud-display-sm text-chocolate-900 mb-4">
            Degerlendirme Sonuclariniz
          </h1>
          <p className="freud-text-lg text-chocolate-500 max-w-2xl mx-auto">
            Yanitlariniza dayali kisisellestirilmis profiliniz. Bu icgoruler, guclu yonlerinizi ve gelisim alanlarinizi anlamaniza yardimci olabilir.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="freud-card-elevated p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                <Brain className="w-5 h-5 text-sage-600" />
              </div>
              <h2 className="freud-heading-xl text-chocolate-900">
                Karakteristik Profil
              </h2>
            </div>
            <ResultsGraphTR scores={scores} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="freud-card-elevated p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-coral-100 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-coral-600" />
              </div>
              <h2 className="freud-heading-xl text-chocolate-900">
                Harmonik Olcek Profili
              </h2>
            </div>
            <HarmonicGraphTR scores={harmonicScores} />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-sage-500 to-sage-600 rounded-3xl p-8 md:p-10 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
          </div>
          <div className="relative z-10">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <h3 className="freud-heading-xl text-white mb-3">
              YZ Destekli Icgorüler Alin
            </h3>
            <p className="freud-text-base text-sage-100 mb-8 max-w-xl mx-auto">
              Yapay zekamiz sonuclarinizi analiz edebilir ve buyume ile gelisim icin kisisellestirilmis oneriler sunabilir.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => navigate('/tr/dashboard')}
                className="inline-flex items-center gap-2 px-8 py-3 bg-white text-sage-700 rounded-xl
                  font-semibold hover:bg-cream-50 transition-all shadow-soft"
              >
                <Sparkles className="w-5 h-5" />
                Kontrol Paneline Git
              </button>
              <button
                onClick={() => navigate('/tr/dashboard')}
                className="inline-flex items-center gap-2 px-8 py-3 bg-sage-400/30 text-white border border-white/30 rounded-xl
                  font-semibold hover:bg-sage-400/50 transition-all"
              >
                Kontrol Paneli
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
